'use client';

import _slicedToArray from "/src/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/src/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["checked", "defaultChecked", "disabled", "onBlur", "onChange", "onFocus", "onFocusVisible", "readOnly", "required", "id", "color", "variant", "size", "startDecorator", "endDecorator", "component", "slots", "slotProps"];
import * as React from 'react';
import { unstable_capitalize as capitalize } from '@mui/utils';
import { unstable_composeClasses as composeClasses } from '@mui/base/composeClasses';
import { useSwitch } from '@mui/base/useSwitch';
import { styled, useThemeProps } from '../styles';
import useSlot from '../utils/useSlot';
import switchClasses, { getSwitchUtilityClass } from './switchClasses';
import FormControlContext from '../FormControl/FormControlContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var useUtilityClasses = function useUtilityClasses(ownerState) {
  var checked = ownerState.checked,
      disabled = ownerState.disabled,
      focusVisible = ownerState.focusVisible,
      readOnly = ownerState.readOnly,
      color = ownerState.color,
      variant = ownerState.variant,
      size = ownerState.size;
  var slots = {
    root: ['root', checked && 'checked', disabled && 'disabled', focusVisible && 'focusVisible', readOnly && 'readOnly', variant && "variant".concat(capitalize(variant)), color && "color".concat(capitalize(color)), size && "size".concat(capitalize(size))],
    thumb: ['thumb', checked && 'checked'],
    track: ['track', checked && 'checked'],
    action: ['action', focusVisible && 'focusVisible'],
    input: ['input'],
    startDecorator: ['startDecorator'],
    endDecorator: ['endDecorator']
  };
  return composeClasses(slots, getSwitchUtilityClass, {});
};

var switchColorVariables = function switchColorVariables(_ref3) {
  var theme = _ref3.theme,
      ownerState = _ref3.ownerState;
  return function () {
    var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _theme$variants, _styles$backgroundCol, _styles$backgroundCol2;

    var styles = ((_theme$variants = theme.variants["".concat(ownerState.variant).concat(data.state || '')]) == null ? void 0 : _theme$variants[ownerState.color]) || {};
    return {
      '--Switch-trackBackground': (_styles$backgroundCol = styles.backgroundColor) != null ? _styles$backgroundCol : theme.vars.palette.background.surface,
      '--Switch-trackColor': styles.color,
      '--Switch-trackBorderColor': ownerState.variant === 'outlined' ? styles.borderColor : 'currentColor',
      '--Switch-thumbBackground': styles.color,
      '--Switch-thumbColor': (_styles$backgroundCol2 = styles.backgroundColor) != null ? _styles$backgroundCol2 : theme.vars.palette.background.surface
    };
  };
};

var SwitchRoot = styled('div', {
  name: 'JoySwitch',
  slot: 'Root',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.root;
  }
})(function (_ref4) {
  var _extends2;

  var theme = _ref4.theme,
      ownerState = _ref4.ownerState;

  var _theme$variants2;

  var getColorVariables = switchColorVariables({
    theme: theme,
    ownerState: ownerState
  });
  return _extends({
    '--Icon-color': 'currentColor',
    '--variant-borderWidth': (_theme$variants2 = theme.variants[ownerState.variant]) == null || (_theme$variants2 = _theme$variants2[ownerState.color]) == null ? void 0 : _theme$variants2['--variant-borderWidth'],
    '--Switch-trackRadius': theme.vars.radius.xl,
    '--Switch-thumbShadow': ownerState.variant === 'soft' ? 'none' : '0 0 0 1px var(--Switch-trackBackground)'
  }, ownerState.size === 'sm' && {
    '--Switch-trackWidth': '26px',
    '--Switch-trackHeight': '16px',
    '--Switch-thumbSize': '10px',
    fontSize: theme.vars.fontSize.sm,
    gap: 'var(--Switch-gap, 6px)'
  }, ownerState.size === 'md' && {
    '--Switch-trackWidth': '32px',
    '--Switch-trackHeight': '20px',
    '--Switch-thumbSize': '14px',
    fontSize: theme.vars.fontSize.md,
    gap: 'var(--Switch-gap, 8px)'
  }, ownerState.size === 'lg' && {
    '--Switch-trackWidth': '40px',
    '--Switch-trackHeight': '24px',
    '--Switch-thumbSize': '18px',
    gap: 'var(--Switch-gap, 12px)'
  }, {
    '--unstable_paddingBlock': "max((var(--Switch-trackHeight) - 2 * var(--variant-borderWidth, 0px) - var(--Switch-thumbSize)) / 2, 0px)",
    '--Switch-thumbRadius': "max(var(--Switch-trackRadius) - var(--unstable_paddingBlock), min(var(--unstable_paddingBlock) / 2, var(--Switch-trackRadius) / 2))",
    '--Switch-thumbWidth': 'var(--Switch-thumbSize)',
    '--Switch-thumbOffset': "max((var(--Switch-trackHeight) - var(--Switch-thumbSize)) / 2, 0px)"
  }, getColorVariables(), (_extends2 = {
    '&:hover': {
      '@media (hover: hover)': _extends({}, getColorVariables({
        state: 'Hover'
      }))
    }
  }, _defineProperty(_extends2, "&.".concat(switchClasses.checked), _extends({}, getColorVariables(), {
    '&:hover': {
      '@media (hover: hover)': _extends({}, getColorVariables({
        state: 'Hover'
      }))
    }
  })), _defineProperty(_extends2, "&.".concat(switchClasses.disabled), _extends({
    pointerEvents: 'none',
    color: theme.vars.palette.text.tertiary
  }, getColorVariables({
    state: 'Disabled'
  }))), _defineProperty(_extends2, "display", 'inline-flex'), _defineProperty(_extends2, "alignItems", 'center'), _defineProperty(_extends2, "alignSelf", 'center'), _defineProperty(_extends2, "fontFamily", theme.vars.fontFamily.body), _defineProperty(_extends2, "position", 'relative'), _defineProperty(_extends2, "padding", 'calc((var(--Switch-thumbSize) / 2) - (var(--Switch-trackHeight) / 2)) calc(-1 * var(--Switch-thumbOffset))'), _defineProperty(_extends2, "backgroundColor", 'initial'), _defineProperty(_extends2, "border", 'none'), _defineProperty(_extends2, "margin", 'var(--unstable_Switch-margin)'), _extends2));
});
var SwitchAction = styled('div', {
  name: 'JoySwitch',
  slot: 'Action',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.action;
  }
})(function (_ref5) {
  var theme = _ref5.theme;
  return _defineProperty({
    borderRadius: 'var(--Switch-trackRadius)',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  }, theme.focus.selector, theme.focus.default);
});
var SwitchInput = styled('input', {
  name: 'JoySwitch',
  slot: 'Input',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.input;
  }
})({
  margin: 0,
  height: '100%',
  width: '100%',
  opacity: 0,
  position: 'absolute',
  cursor: 'pointer'
});
var SwitchTrack = styled('span', {
  name: 'JoySwitch',
  slot: 'Track',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.track;
  }
})(function (_ref7) {
  var theme = _ref7.theme,
      ownerState = _ref7.ownerState;
  return _extends({
    position: 'relative',
    color: 'var(--Switch-trackColor)',
    height: 'var(--Switch-trackHeight)',
    width: 'var(--Switch-trackWidth)',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    border: 'var(--variant-borderWidth, 0px) solid',
    borderColor: 'var(--Switch-trackBorderColor)',
    backgroundColor: 'var(--Switch-trackBackground)',
    borderRadius: 'var(--Switch-trackRadius)',
    fontFamily: theme.vars.fontFamily.body
  }, ownerState.size === 'sm' && {
    fontSize: theme.vars.fontSize.xs
  }, ownerState.size === 'md' && {
    fontSize: theme.vars.fontSize.sm
  }, ownerState.size === 'lg' && {
    fontSize: theme.vars.fontSize.md
  });
});
var SwitchThumb = styled('span', {
  name: 'JoySwitch',
  slot: 'Thumb',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.thumb;
  }
})(_defineProperty({
  '--Icon-fontSize': 'calc(var(--Switch-thumbSize) * 0.75)',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '50%',
  left: 'calc(50% - var(--Switch-trackWidth) / 2 + var(--Switch-thumbWidth) / 2 + var(--Switch-thumbOffset))',
  transform: 'translate(-50%, -50%)',
  width: 'var(--Switch-thumbWidth)',
  height: 'var(--Switch-thumbSize)',
  borderRadius: 'var(--Switch-thumbRadius)',
  boxShadow: 'var(--Switch-thumbShadow)',
  color: 'var(--Switch-thumbColor)',
  backgroundColor: 'var(--Switch-thumbBackground)'
}, "&.".concat(switchClasses.checked), {
  left: 'calc(50% + var(--Switch-trackWidth) / 2 - var(--Switch-thumbWidth) / 2 - var(--Switch-thumbOffset))'
}));
var SwitchStartDecorator = styled('span', {
  name: 'JoySwitch',
  slot: 'StartDecorator',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.startDecorator;
  }
})({
  display: 'inline-flex'
});
var SwitchEndDecorator = styled('span', {
  name: 'JoySwitch',
  slot: 'EndDecorator',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.endDecorator;
  }
})({
  display: 'inline-flex'
});
/**
 *
 * Demos:
 *
 * - [Switch](https://mui.com/joy-ui/react-switch/)
 *
 * API:
 *
 * - [Switch API](https://mui.com/joy-ui/api/switch/)
 */

var Switch = /*#__PURE__*/React.forwardRef(function Switch(inProps, ref) {
  var _ref, _inProps$size, _inProps$color, _formControl$color, _ref2, _inProps$disabled;

  var props = useThemeProps({
    props: inProps,
    name: 'JoySwitch'
  });

  var disabledExternalProp = props.disabled,
      id = props.id,
      colorProp = props.color,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'solid' : _props$variant,
      _props$size = props.size,
      sizeProp = _props$size === void 0 ? 'md' : _props$size,
      startDecorator = props.startDecorator,
      endDecorator = props.endDecorator,
      component = props.component,
      _props$slots = props.slots,
      slots = _props$slots === void 0 ? {} : _props$slots,
      _props$slotProps = props.slotProps,
      slotProps = _props$slotProps === void 0 ? {} : _props$slotProps,
      other = _objectWithoutPropertiesLoose(props, _excluded);

  var formControl = React.useContext(FormControlContext);

  if (process.env.NODE_ENV !== 'production') {
    var registerEffect = formControl == null ? void 0 : formControl.registerEffect; // eslint-disable-next-line react-hooks/rules-of-hooks

    React.useEffect(function () {
      if (registerEffect) {
        return registerEffect();
      }

      return undefined;
    }, [registerEffect]);
  }

  var size = (_ref = (_inProps$size = inProps.size) != null ? _inProps$size : formControl == null ? void 0 : formControl.size) != null ? _ref : sizeProp;
  var color = (_inProps$color = inProps.color) != null ? _inProps$color : formControl != null && formControl.error ? 'danger' : (_formControl$color = formControl == null ? void 0 : formControl.color) != null ? _formControl$color : colorProp;

  var useSwitchProps = _extends({
    disabled: (_ref2 = (_inProps$disabled = inProps.disabled) != null ? _inProps$disabled : formControl == null ? void 0 : formControl.disabled) != null ? _ref2 : disabledExternalProp
  }, props);

  var _useSwitch = useSwitch(useSwitchProps),
      getInputProps = _useSwitch.getInputProps,
      checked = _useSwitch.checked,
      disabled = _useSwitch.disabled,
      focusVisible = _useSwitch.focusVisible,
      readOnly = _useSwitch.readOnly;

  var ownerState = _extends({}, props, {
    id: id,
    checked: checked,
    disabled: disabled,
    focusVisible: focusVisible,
    readOnly: readOnly,
    color: checked ? color || 'primary' : color || 'neutral',
    variant: variant,
    size: size
  });

  var classes = useUtilityClasses(ownerState);

  var externalForwardedProps = _extends({}, other, {
    component: component,
    slots: slots,
    slotProps: slotProps
  });

  var _useSlot = useSlot('root', {
    ref: ref,
    className: classes.root,
    elementType: SwitchRoot,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot2 = _slicedToArray(_useSlot, 2),
      SlotRoot = _useSlot2[0],
      rootProps = _useSlot2[1];

  var _useSlot3 = useSlot('startDecorator', {
    additionalProps: {
      'aria-hidden': true // hide the decorator from assistive technology

    },
    className: classes.startDecorator,
    elementType: SwitchStartDecorator,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot4 = _slicedToArray(_useSlot3, 2),
      SlotStartDecorator = _useSlot4[0],
      startDecoratorProps = _useSlot4[1];

  var _useSlot5 = useSlot('endDecorator', {
    additionalProps: {
      'aria-hidden': true // hide the decorator from assistive technology

    },
    className: classes.endDecorator,
    elementType: SwitchEndDecorator,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot6 = _slicedToArray(_useSlot5, 2),
      SlotEndDecorator = _useSlot6[0],
      endDecoratorProps = _useSlot6[1];

  var _useSlot7 = useSlot('track', {
    className: classes.track,
    elementType: SwitchTrack,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot8 = _slicedToArray(_useSlot7, 2),
      SlotTrack = _useSlot8[0],
      trackProps = _useSlot8[1];

  var _useSlot9 = useSlot('thumb', {
    className: classes.thumb,
    elementType: SwitchThumb,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot10 = _slicedToArray(_useSlot9, 2),
      SlotThumb = _useSlot10[0],
      thumbProps = _useSlot10[1];

  var _useSlot11 = useSlot('action', {
    className: classes.action,
    elementType: SwitchAction,
    externalForwardedProps: externalForwardedProps,
    ownerState: ownerState
  }),
      _useSlot12 = _slicedToArray(_useSlot11, 2),
      SlotAction = _useSlot12[0],
      actionProps = _useSlot12[1];

  var _useSlot13 = useSlot('input', {
    additionalProps: {
      id: id != null ? id : formControl == null ? void 0 : formControl.htmlFor,
      'aria-describedby': formControl == null ? void 0 : formControl['aria-describedby']
    },
    className: classes.input,
    elementType: SwitchInput,
    externalForwardedProps: externalForwardedProps,
    getSlotProps: getInputProps,
    ownerState: ownerState
  }),
      _useSlot14 = _slicedToArray(_useSlot13, 2),
      SlotInput = _useSlot14[0],
      inputProps = _useSlot14[1];

  return /*#__PURE__*/_jsxs(SlotRoot, _extends({}, rootProps, {
    children: [startDecorator && /*#__PURE__*/_jsx(SlotStartDecorator, _extends({}, startDecoratorProps, {
      children: typeof startDecorator === 'function' ? startDecorator(ownerState) : startDecorator
    })), /*#__PURE__*/_jsxs(SlotTrack, _extends({}, trackProps, {
      children: [trackProps == null ? void 0 : trackProps.children, /*#__PURE__*/_jsx(SlotThumb, _extends({}, thumbProps))]
    })), /*#__PURE__*/_jsx(SlotAction, _extends({}, actionProps, {
      children: /*#__PURE__*/_jsx(SlotInput, _extends({}, inputProps))
    })), endDecorator && /*#__PURE__*/_jsx(SlotEndDecorator, _extends({}, endDecoratorProps, {
      children: typeof endDecorator === 'function' ? endDecorator(ownerState) : endDecorator
    }))]
  }));
});
process.env.NODE_ENV !== "production" ? void 0 : void 0;
export default Switch;